import React, { FC, useState, useEffect } from 'react'
import { useLocation, useParams, useHistory } from 'react-router'
import { Box, Text, Heading, Flex } from '@chakra-ui/core'
import { useRecoilValue, useRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { configState } from '../../recoil/atoms'
import { LinkButton } from '../../lib/types'
import { useQuery } from '@apollo/client'
import { Counter, ImageBox } from '@engyalo/storefront-components'
import { Price } from '../../components/Price'
import { Hook } from '@engyalo/storefront-types'
import { cartState } from '../../recoil/atoms'
import Plugin from '../../Plugin'
import { ICartDictionary, ICartStats, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { cartStatsState } from '../../recoil/selectors'
import { searchProductsByIdQuery } from '../../queries'
import {
    IProductsPricedPerCollectionVariables,
    IProductsPricedPerCollection,
    IProductPricedPerCollectionLevel1 as productLevel1Type,
} from '@engyalo/storefront-types/lib/graphql/product'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'

export const Product: FC = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const { customer, order } = useParams()
    const config = useRecoilValue(configState)
    const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
    const [canAddToCart, setCanAddToCart] = useState(true)
    const cartStats = useRecoilValue<ICartStats>(cartStatsState)
    const [productToAddToCart, setproductToAdd] = useState({} as IProduct)
    const [priceEnabled, setPriceEnabled] = useState(false)
    const [productData, setProductData] = useState({} as productLevel1Type)

    const {
        state: { product },
    } = useLocation()

    useEffect((): void => {
        setproductToAdd({
            ...product,
            cartId: generateCartId(product),
        })
    }, [product])

    const { loading: loadingProduct } = useQuery<IProductsPricedPerCollection, IProductsPricedPerCollectionVariables>(
        searchProductsByIdQuery,
        {
            variables: {
                name: customer,
                configId: order,
                productId: product.id,
            },
            skip: !customer,
            onCompleted(data: IProductsPricedPerCollection) {
                const currentCollection = data?.productsPricedPerCollection?.[0]

                setPriceEnabled(currentCollection?.enabledPrice || true)
                setProductData(currentCollection?.productsPriced?.[0] ?? productData)
            },
            returnPartialData: true,
            fetchPolicy: 'no-cache',
        }
    )

    const getProductFirstImage = (product: productLevel1Type): string => {
        if (product && product.imageURL) return product.imageURL[0] || ''
        return ''
    }

    const addToCart = (product: productLevel1Type, adder = 1, backToCatalogue = false): void => {
        if (canAddToCart) {
            const tempCart = { ...cart }
            if (product && product.id) {
                const currentSku = generateCartId(product)
                if (tempCart[currentSku]) {
                    const tempProd = { ...tempCart[currentSku] }
                    console.log(tempProd, adder)
                    if (tempProd.quantity) {
                        const totalPrice = getTotalPrice(product)
                        tempProd.quantity += adder

                        if (tempProd.quantity > 0) {
                            tempCart[currentSku] = {
                                ...tempProd,
                                totalPrice,
                            }
                            setCart(tempCart)
                        } else {
                            delete tempCart[currentSku]
                            setCart({ ...tempCart })
                        }
                    }
                } else if (adder > 0) {
                    const totalPrice = getTotalPrice(product)

                    tempCart[currentSku] = {
                        ...product,
                        totalPrice,
                        quantity: adder,
                        cartId: currentSku,
                    } as IProduct
                    setCart(tempCart)
                }

                if (backToCatalogue) {
                    history.push({
                        pathname: `/${customer}/${order}`,
                    })
                }
            }
        }
    }

    if (product) {
        return (
            <Box h="100%" w="100%" d="flex" flexDir="column">
                <Box h="40px" d="flex" justifyContent="space-between" paddingX="12px">
                    <LinkButton
                        to={`/${customer}/${order}`}
                        h="100%"
                        w="32px"
                        background="transparent"
                        borderColor="transparent"
                        size="lg"
                        aria-label="cart"
                        icon="chevron-left"
                    />
                    <LinkButton
                        to={`/${customer}/${order}/cart`}
                        h="100%"
                        w="32px"
                        background="transparent"
                        borderColor="transparent"
                        size="lg"
                        aria-label="cart"
                        icon="settings"
                    />
                </Box>
                {/* Body */}
                <Plugin
                    hook={Hook.PRODUCT_DETAIL_BODY}
                    pluginProps={{
                        product: productData,
                        isLoading: loadingProduct,
                        cart,
                        config,
                        enablePrice: priceEnabled,
                        setCanAddToCart,
                        tFunction: t,
                        setproductToAdd,
                    }}
                >
                    <>
                        <Box paddingX="16px">
                            <ImageBox
                                imageUrl={getProductFirstImage(productToAddToCart)}
                                quantity={
                                    cart[productToAddToCart.cartId || '']
                                        ? cart[productToAddToCart.cartId || ''].quantity
                                        : 0
                                }
                            />
                            <Heading marginY="4px" as="h3" fontSize="xl" flex="1">
                                {productToAddToCart.name}
                            </Heading>
                            <Text fontSize="md" fontFamily="body" color="gray.100" paddingBottom="4px">
                                {productToAddToCart.description}
                            </Text>
                            <Price price={productToAddToCart.price} currency="Q." />
                        </Box>
                        <Flex flex="1" flexDirection="column" overflow="auto" marginTop="8px" paddingX="4px"></Flex>
                    </>
                </Plugin>
                {/* Body */}
                {/* Footer */}
                <Plugin
                    hook={Hook.PRODUCT_ADD_TO_CART}
                    pluginProps={{
                        product: productToAddToCart,
                        config,
                        addToCart,
                        cartStats,
                        cart,
                        tFunction: t,
                        canAddToCart,
                    }}
                >
                    <Box zIndex={1} paddingY="12px" paddingX="8px" boxShadow="md" backgroundColor="white">
                        <Box w="50%" m="auto">
                            <Counter
                                name={productToAddToCart.cartId || ''}
                                addAction={(e): void => {
                                    e.stopPropagation()
                                    addToCart(productToAddToCart, 1)
                                }}
                                minusAction={(e): void => {
                                    e.stopPropagation()
                                    addToCart(productToAddToCart, -1)
                                }}
                                value={
                                    cart[productToAddToCart.cartId || '']
                                        ? cart[productToAddToCart.cartId || ''].quantity
                                        : 0
                                }
                            />
                        </Box>
                    </Box>
                </Plugin>
                {/* Footer */}
            </Box>
        )
    }
    return <div> no product</div>
}
